import React, { useState } from "react";
import axios from "axios";
import { X, Sparkles, Check, AlertCircle, RefreshCw } from "lucide-react";
import { resolveImageUrl } from "../../components/ImageUpload";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const CONCURRENCY = 2; // Gemini отвечает ~20 с: два параллельных потока держат темп и не ловят лимиты

/**
 * Массовая перегенерация описаний по фото.
 * Генерация идёт в браузере по одному товару (тот же эндпоинт, что и в карточке),
 * поэтому ничего не падает по таймауту и видно прогресс. Сохранение — только
 * по кнопке и только для отмеченных товаров.
 */
export default function BulkDescribeModal({ products, onClose, onSaved }) {
  const [items, setItems] = useState(() => products.map(p => ({
    id: p.id,
    name: p.name,
    image: p.images?.[0] || "",
    oldDesc: p.description || "",
    newDesc: "",
    status: "pending",
    error: "",
    apply: true,
    product: p,
  })));
  const [running, setRunning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedCount, setSavedCount] = useState(0);

  const patch = (id, fields) => setItems(prev => prev.map(it => it.id === id ? { ...it, ...fields } : it));

  const generateOne = async (it) => {
    const p = it.product;
    if (!it.image) {
      patch(it.id, { status: "error", error: "Нет фото", apply: false });
      return;
    }
    patch(it.id, { status: "running", error: "" });
    try {
      const res = await axios.post(`${API}/admin/products/ai-describe`, {
        image_url: it.image,
        name: p.name || undefined,
        category: p.category || undefined,
        brand: p.brand || undefined,
        price: p.price || undefined,
        material: p.material || undefined,
      }, { withCredentials: true });
      const d = res.data || {};
      patch(it.id, { status: d.description ? "done" : "error", newDesc: d.description || "",
                     error: d.description ? "" : "Пустой ответ", apply: Boolean(d.description) });
    } catch (err) {
      patch(it.id, { status: "error", apply: false,
                     error: err.response?.data?.detail || "Ошибка генерации" });
    }
  };

  const runAll = async (targets) => {
    setRunning(true);
    const queue = [...targets];
    const worker = async () => {
      while (queue.length) {
        const next = queue.shift();
        await generateOne(next);
      }
    };
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, queue.length) }, worker));
    setRunning(false);
  };

  const saveSelected = async () => {
    const toSave = items.filter(it => it.apply && it.newDesc.trim());
    if (!toSave.length) return;
    setSaving(true);
    let ok = 0;
    for (const it of toSave) {
      try {
        await axios.put(`${API}/products/${it.id}`, { description: it.newDesc.trim() }, { withCredentials: true });
        ok += 1;
        patch(it.id, { status: "saved" });
      } catch {
        patch(it.id, { status: "error", error: "Не удалось сохранить" });
      }
    }
    setSavedCount(ok);
    setSaving(false);
    onSaved?.(ok);
  };

  const doneCount = items.filter(it => it.status === "done" || it.status === "saved").length;
  const readyToSave = items.filter(it => it.apply && it.newDesc.trim()).length;
  const pending = items.filter(it => it.status === "pending" || it.status === "error");

  return (
    <div data-testid="bulk-describe-modal"
      style={{ position: "fixed", inset: 0, zIndex: 120, background: "rgba(10,10,10,0.75)",
               display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "16px", overflowY: "auto" }}>
      <div style={{ background: "#fff", borderRadius: "14px", width: "100%", maxWidth: "860px",
                    fontFamily: "Manrope", boxShadow: "0 24px 60px rgba(0,0,0,0.4)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                      padding: "16px 18px", borderBottom: "1px solid #EEE7DC", position: "sticky", top: 0, background: "#fff", borderRadius: "14px 14px 0 0" }}>
          <div>
            <div style={{ fontFamily: "Cormorant Garamond", fontSize: "20px", color: "#4A1410" }}>
              Описания по фото — {items.length} товаров
            </div>
            <div style={{ fontSize: "12px", color: "#9A7B55" }}>
              Готово {doneCount} из {items.length}. Проверьте текст и отметьте, что применить.
            </div>
          </div>
          <button data-testid="bulk-describe-close" onClick={onClose} disabled={running || saving}
            style={{ background: "transparent", border: "none", cursor: running || saving ? "wait" : "pointer", color: "#4A1410" }}>
            <X size={20} />
          </button>
        </div>

        <div style={{ padding: "14px 18px", display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center" }}>
          <button data-testid="bulk-describe-start" onClick={() => runAll(pending)} disabled={running || saving || !pending.length}
            style={{ background: "#4A1410", color: "#F3E3C8", border: "none", borderRadius: "8px",
                     padding: "10px 16px", fontSize: "12px", fontWeight: 700, letterSpacing: "0.05em",
                     textTransform: "uppercase", cursor: running ? "wait" : "pointer", opacity: !pending.length ? 0.5 : 1,
                     display: "inline-flex", alignItems: "center", gap: "8px" }}>
            <Sparkles size={14} /> {running ? "Генерируем…" : pending.length === items.length ? "Сгенерировать все" : `Догенерировать (${pending.length})`}
          </button>
          <button data-testid="bulk-describe-save" onClick={saveSelected} disabled={running || saving || !readyToSave}
            style={{ background: readyToSave ? "linear-gradient(135deg,#C5A059,#A5803C)" : "#E5E0D5",
                     color: readyToSave ? "#1A1A1A" : "#9A9A9A", border: "none", borderRadius: "8px",
                     padding: "10px 16px", fontSize: "12px", fontWeight: 800, letterSpacing: "0.05em",
                     textTransform: "uppercase", cursor: saving ? "wait" : "pointer",
                     display: "inline-flex", alignItems: "center", gap: "8px" }}>
            <Check size={14} /> {saving ? "Сохраняем…" : `Сохранить выбранные (${readyToSave})`}
          </button>
          {savedCount > 0 && (
            <span data-testid="bulk-describe-saved" style={{ fontSize: "12px", color: "#2E7D32", fontWeight: 600 }}>
              Сохранено: {savedCount}
            </span>
          )}
        </div>

        <div style={{ padding: "0 18px 18px", display: "flex", flexDirection: "column", gap: "14px" }}>
          {items.map(it => (
            <div key={it.id} data-testid={`bulk-describe-item-${it.id}`}
              style={{ border: "1px solid #EEE7DC", borderRadius: "10px", padding: "12px", background: "#FDFBF7" }}>
              <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
                <div style={{ width: "64px", height: "84px", flexShrink: 0, borderRadius: "6px",
                              overflow: "hidden", background: "#EFE7DA" }}>
                  {it.image
                    ? <img src={resolveImageUrl(it.image, { w: 200 })} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    : null}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", justifyContent: "space-between" }}>
                    <div style={{ fontSize: "13px", fontWeight: 700, color: "#2A1810" }}>{it.name}</div>
                    <label style={{ fontSize: "11px", color: "#6B5A45", display: "inline-flex", alignItems: "center", gap: "6px", whiteSpace: "nowrap" }}>
                      <input data-testid={`bulk-describe-apply-${it.id}`} type="checkbox" checked={it.apply}
                        disabled={!it.newDesc}
                        onChange={e => patch(it.id, { apply: e.target.checked })} />
                      Применить
                    </label>
                  </div>
                  <div style={{ fontSize: "11px", color: "#9A7B55", margin: "4px 0 8px" }}>
                    Было: {it.oldDesc ? `${it.oldDesc.slice(0, 120)}${it.oldDesc.length > 120 ? "…" : ""}` : "описания нет"}
                  </div>
                  {it.status === "running" && (
                    <div style={{ fontSize: "12px", color: "#4A1410", display: "inline-flex", alignItems: "center", gap: "6px" }}>
                      <RefreshCw size={13} className="spin" /> Генерируем описание…
                    </div>
                  )}
                  {it.error && (
                    <div data-testid={`bulk-describe-error-${it.id}`}
                      style={{ fontSize: "12px", color: "#B3261E", display: "inline-flex", alignItems: "center", gap: "6px" }}>
                      <AlertCircle size={13} /> {it.error}
                    </div>
                  )}
                  {it.newDesc && (
                    <textarea data-testid={`bulk-describe-text-${it.id}`} value={it.newDesc}
                      onChange={e => patch(it.id, { newDesc: e.target.value })}
                      rows={10}
                      style={{ width: "100%", border: "1px solid #E0D6C4", borderRadius: "8px", padding: "10px",
                               fontSize: "13px", lineHeight: 1.6, color: "#2A1810", background: "#fff",
                               fontFamily: "Manrope", resize: "vertical" }} />
                  )}
                  {it.status === "saved" && (
                    <div style={{ fontSize: "12px", color: "#2E7D32", fontWeight: 600, marginTop: "6px" }}>
                      Сохранено в карточку
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
